/**
 * Exponential backoff utility for retry logic
 */

export interface BackoffOptions {
  initialDelay?: number;
  maxDelay?: number;
  multiplier?: number;
  maxRetries?: number;
  jitter?: boolean;
}

const DEFAULT_OPTIONS: Required<BackoffOptions> = {
  initialDelay: 1000,
  maxDelay: 30000,
  multiplier: 2,
  maxRetries: 3,
  jitter: true,
};

export class ExponentialBackoff {
  private options: Required<BackoffOptions>;
  private attempt: number = 0;

  constructor(options: BackoffOptions = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  /**
   * Get the next delay in milliseconds
   */
  getNextDelay(): number {
    if (this.attempt >= this.options.maxRetries) {
      throw new Error("Max retries exceeded");
    }

    const delay = Math.min(
      this.options.initialDelay *
        Math.pow(this.options.multiplier, this.attempt),
      this.options.maxDelay,
    );

    this.attempt++;

    // Add jitter to prevent thundering herd
    if (this.options.jitter) {
      return delay * (0.5 + Math.random() * 0.5);
    }

    return delay;
  }

  /**
   * Reset the backoff counter
   */
  reset(): void {
    this.attempt = 0;
  }

  /**
   * Check if retries are exhausted
   */
  isExhausted(): boolean {
    return this.attempt >= this.options.maxRetries;
  }

  /**
   * Get the current attempt number
   */
  getAttempt(): number {
    return this.attempt;
  }
}

/**
 * Execute a function with exponential backoff retry
 */
export async function withExponentialBackoff<T>(
  fn: () => Promise<T>,
  options: BackoffOptions = {},
  shouldRetry?: (error: any) => boolean,
): Promise<T> {
  const backoff = new ExponentialBackoff(options);

  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await fn();
    } catch (error) {
      // Check if we should retry this error
      if (shouldRetry && !shouldRetry(error)) {
        throw error;
      }

      if (backoff.isExhausted()) {
        throw error;
      }

      const delay = backoff.getNextDelay();
      console.log(
        `Retrying after ${delay}ms (attempt ${backoff.getAttempt()})`,
      );

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}

/**
 * Check if an error is retryable
 */
export function isRetryableError(error: any): boolean {
  // Network errors are retryable
  if (error.code === "ECONNREFUSED" || error.code === "ETIMEDOUT") {
    return true;
  }

  // 5xx errors are retryable
  if (error.response?.status >= 500) {
    return true;
  }

  // 429 (rate limit) is retryable
  if (error.response?.status === 429) {
    return true;
  }

  // Specific error messages that indicate temporary issues
  const retryableMessages = [
    "network error",
    "timeout",
    "ECONNRESET",
    "socket hang up",
  ];

  const errorMessage = error.message?.toLowerCase() || "";
  return retryableMessages.some((msg) => errorMessage.includes(msg));
}
